export type ProjectStatus = 'live' | 'launching' | 'development' | 'deployed';
export type ProjectCategory =
  | 'saas'
  | 'fintech'
  | 'gaming'
  | 'compliance'
  | 'gamedev'
  | 'systems'
  | 'freelance';

export type MockupKind = 'browser' | 'phone';

export interface FeatureDetail {
  title: string;
  description: string;
}

export interface Project {
  slug: string;
  name: string;
  initials: string;
  tagline: string;
  description: string;
  domain: string | null;
  status: ProjectStatus;
  statusLabel: string;
  category: ProjectCategory;
  categoryLabel: string;
  accentColor: string;
  gradient: string;
  stack: string[];
  features: string[];
  featureDetails: FeatureDetail[];
  challenge: string;
  approach: string;
  results: string;
  metrics: string;
  featured: boolean;
  order: number;
  mockup: MockupKind;
  liveUrl: string | null;
  links?: { label: string; url: string }[];
}

export const PROJECTS: Project[] = [
  {
    slug: 'powderledger',
    name: 'PowderLedger',
    initials: 'PL',
    tagline: 'Ski & Snowboard Rental Management',
    description:
      'Full-stack rental shop management platform with ISO 11088 DIN calculator, real-time inventory, service center kanban board, QR scan & check-in with live camera, multi-tenant architecture, and analytics dashboard. Currently in active use at PTO Ski & Snowboarding.',
    domain: 'powderledger.com',
    status: 'live',
    statusLabel: 'LIVE USERS',
    category: 'saas',
    categoryLabel: 'SaaS',
    accentColor: 'var(--powder-blue)',
    gradient: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
    stack: ['Next.js', 'Prisma', 'Neon', 'NextAuth', 'Tailwind', 'TypeScript', 'Vercel'],
    features: [
      'ISO 11088 DIN Calculator for binding safety',
      'Real-time inventory management with add/edit wizards',
      'Service center kanban workflow board',
      'Live camera QR scan & check-in (@zxing/browser)',
      'Customer profiles with waiver modals',
      'Multi-tenant architecture with role-based access',
      'Analytics dashboard with business metrics',
    ],
    metrics: 'Active customer: PTO Ski & Snowboarding',
    challenge:
      "Ski and snowboard rental shops run on paper forms, spreadsheets, and memory. Equipment tracking is manual, DIN settings are calculated by hand (risking safety errors), and there's no unified system for managing customers, inventory, and service across a busy rental floor. PTO Ski & Snowboarding needed a modern platform that could handle their entire operation from check-in to check-out.",
    approach:
      'I built PowderLedger as a full multi-tenant SaaS platform from scratch, no templates, no boilerplate. The architecture supports multiple shop organizations with role-based access, real-time inventory state, and a service workflow that mirrors how rental techs actually work. The DIN calculator implements the full ISO 11088 standard so binding settings are always safe and accurate. For check-in, I integrated the device camera with @zxing/browser to scan QR codes instantly, no external hardware needed.',
    results:
      'PowderLedger is in active use at PTO Ski & Snowboarding, handling real customer check-ins and equipment management. The DIN calculator alone eliminates a major safety liability. The platform replaces what would typically require 3-4 separate tools (spreadsheet, calculator, intake forms, service tracker) with a single unified interface.',
    featureDetails: [
      {
        title: 'ISO 11088 DIN Calculator',
        description:
          'Automated binding safety calculations based on skier type, height, weight, boot sole length, and age, replacing error-prone manual charts.',
      },
      {
        title: 'QR Scan & Check-In',
        description:
          "Uses the device's native camera via @zxing/browser library to scan customer QR codes for instant identification and rental lookup, no external scanner hardware required.",
      },
      {
        title: 'Service Center Kanban',
        description:
          'Drag-and-drop kanban board for tracking equipment through service stages: intake, in-progress, quality check, ready for pickup.',
      },
      {
        title: 'Real-Time Inventory',
        description:
          'Full CRUD inventory management with add/edit wizards, status tracking, and availability forecasting across equipment types.',
      },
      {
        title: 'Multi-Tenant Architecture',
        description:
          'Supports multiple shop organizations with isolated data, role-based access control, and organization-specific configuration.',
      },
      {
        title: 'Analytics Dashboard',
        description:
          'Business metrics including rental volume, revenue tracking, popular equipment, and peak hours, data-driven decision making.',
      },
    ],
    featured: true,
    order: 1,
    mockup: 'browser',
    liveUrl: 'https://powderledger.com',
  },
  {
    slug: 'fetti',
    name: 'Fetti',
    initials: 'FT',
    tagline: 'Money Tracker for Cash Workers, receipt OCR on Anthropic vision',
    description:
      "Mobile-first PWA for cash-income workers, servers, bartenders, valets, dancers, drivers, and stylists. Shift logging, expense tracking with receipt OCR, tax estimation, and a goal-driven retention loop that turns financial visibility into a daily habit. Live at fetti.tips.",
    domain: 'fetti.tips',
    status: 'live',
    statusLabel: 'LIVE',
    category: 'fintech',
    categoryLabel: 'FinTech',
    accentColor: 'var(--bag-amber)',
    gradient: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    stack: ['Next.js', 'Prisma', 'Neon', 'Stripe', 'NextAuth', 'Resend', 'Tailwind'],
    features: [
      'Shift logger with earnings + tip-out tracking',
      'Receipt OCR via Anthropic (auto-fills expense forms)',
      'Tax estimator with deductible-category modeling',
      'Magic-link auth, opt-in passwordless flow',
      'Stripe subscription billing for Pro',
      'PWA with offline support, install-to-home-screen',
      "Streaks, goals, and retention loops built in",
    ],
    metrics: 'Live at fetti.tips · serving cash-income workers',
    challenge:
      "There are millions of US workers, bartenders, servers, dancers, valets, drivers, whose income lands in cash or tips. Most have no financial tracking system. They don't know their true hourly rate, can't categorize expenses for tax purposes, and have zero visibility into their earning patterns. Generic finance apps assume W-2 paychecks and bank-integration onboarding. Cash workers need something built specifically for them.",
    approach:
      "Fetti is mobile-first as a PWA, no app store friction, works offline, installs to the home screen. The core UX is a shift logger you can run mid-shift: clock in, log earnings and tip-outs as you go, clock out with a take-home breakdown. Receipt OCR (Anthropic) auto-fills expense forms, snap a photo, get a draft entry. The tax estimator categorizes deductibles by occupation. Streaks, goals, and a daily 'recap' loop turn financial visibility into a habit, not a chore.",
    results:
      "Fetti is live at fetti.tips with active users, an integrated /api/health probe for ops monitoring, deployed to Vercel with continuous post-deploy health checks. The product is in retention mode, interaction-event analytics instrumentation, scrolling persona rail on the landing page, and per-occupation onboarding flows ship weekly.",
    featureDetails: [
      {
        title: 'Shift Logger',
        description:
          'Clock in/out with real-time earnings tracking, tip-out logging, and automatic hourly rate calculation per shift.',
      },
      {
        title: 'Receipt OCR',
        description:
          'Snap a photo of a receipt; Anthropic vision parses date, vendor, total, and category into a draft expense entry, saves it whether you confirm or not.',
      },
      {
        title: 'Tax Estimator',
        description:
          'Projects quarterly and annual tax obligations with per-category deductible toggles tuned for cash-worker occupations.',
      },
      {
        title: 'Magic-Link Auth',
        description:
          'Passwordless email authentication for privacy-first onboarding, no passwords to remember or compromise.',
      },
      {
        title: 'Stripe Billing',
        description:
          'Subscription management with free trial, monthly/annual plans, and graceful upgrade/downgrade flows.',
      },
      {
        title: 'PWA Architecture',
        description:
          'Installable to home screen, works offline, push notifications, native app experience without app store barriers.',
      },
    ],
    featured: true,
    order: 2,
    mockup: 'phone',
    liveUrl: 'https://fetti.tips',
  },
  {
    slug: 'tablesharp',
    name: 'TableSharp',
    initials: 'TS',
    tagline: 'Casino Strategy Trainer',
    description:
      "Comprehensive casino strategy training platform covering blackjack with card counting, craps, video poker, roulette, baccarat, and poker. Features a closed-loop Train → Play → Track → Analyze system with a proprietary 'Money Saved' metric as the primary retention hook.",
    domain: 'tablesharp.com',
    status: 'launching',
    statusLabel: 'LAUNCHING',
    category: 'gaming',
    categoryLabel: 'Gaming',
    accentColor: 'var(--table-purple)',
    gradient: 'linear-gradient(135deg, #a855f7, #ec4899)',
    stack: ['Next.js', 'TypeScript', 'Prisma', 'Neon', 'Stripe', 'Vercel'],
    features: [
      'Blackjack trainer with card counting drills',
      'Craps, video poker, roulette, baccarat modules',
      'Train → Play → Track → Analyze closed loop',
      "Proprietary 'Money Saved' metric",
      'Duolingo-style gamification (streaks, XP)',
      'At-table quick reference mode for live casino use',
    ],
    metrics: '"Play sharp. Leave sharp.", $7.99/mo',
    challenge:
      "Casino players lose money because they play on emotion instead of mathematics. Existing 'strategy trainers' are fragmented, one app for blackjack, another for craps, none connected. There's no unified system that teaches strategy, lets you practice, tracks your play sessions, and shows you how much your improved skills are actually saving you. The gap between 'knowing basic strategy exists' and 'executing it under pressure at a live table' is where money is lost.",
    approach:
      "TableSharp implements a closed-loop learning system: Train (drill correct decisions under time pressure) → Play (simulated sessions with realistic conditions) → Track (log real casino sessions) → Analyze (see your accuracy, deviation patterns, and the proprietary 'Money Saved' metric). The Money Saved metric is the key innovation, it calculates the expected loss difference between your actual play and perfect basic strategy, showing users concrete dollar amounts they're saving by playing better. Gamification (Duolingo-style streaks, XP, levels) drives daily engagement.",
    results:
      "TableSharp is positioning for launch at $7.99/month with the tagline 'Play sharp. Leave sharp.' The at-table quick reference mode, a discreet interface optimized for glancing at during live play, is a key differentiator no competitor offers.",
    featureDetails: [
      {
        title: 'Blackjack with Card Counting',
        description:
          'Full basic strategy trainer plus Hi-Lo card counting drills with running count, true count, and bet sizing practice.',
      },
      {
        title: 'Multi-Game Coverage',
        description:
          "Craps (pass/don't pass, odds, place bets), video poker (Jacks or Better, Deuces Wild), roulette, baccarat, poker basics.",
      },
      {
        title: 'Money Saved Metric',
        description:
          'Proprietary calculation showing the dollar difference between your actual play and mathematically perfect strategy, the primary viral and retention hook.',
      },
      {
        title: 'Duolingo-Style Gamification',
        description:
          'Daily streaks, XP progression, skill levels, and achievements that drive habitual practice and long-term retention.',
      },
      {
        title: 'At-Table Quick Reference',
        description:
          'Discreet, dark-themed reference mode optimized for glancing at during live casino play, shows correct moves for your current hand.',
      },
      {
        title: 'Session Tracking',
        description:
          'Log real casino sessions with buy-in, cash-out, duration, and notes, builds a complete picture of your playing history.',
      },
    ],
    featured: true,
    order: 3,
    mockup: 'browser',
    liveUrl: 'https://tablesharp.com',
  },
  {
    slug: 'ferrodb',
    name: 'FerroDB',
    initials: 'FD',
    tagline: 'A relational database built from scratch in Rust, now AI-native with an HNSW vector index',
    description:
      'A relational database written from scratch in Rust, page-based storage with checksums, a buffer pool, B+-tree indexes, write-ahead logging that survives crash-injection tests, MVCC snapshot isolation, a System-R-style cost-based optimizer with EXPLAIN, and enough of the PostgreSQL wire protocol that a real psql client connects. Its ninth milestone makes it AI-native: an HNSW approximate-nearest-neighbor index with filtered k-NN search and hand-written AVX2 SIMD distance kernels. Public on GitHub with green CI, a 122-test suite, a published architecture book, and a live WebAssembly playground.',
    domain: null,
    status: 'deployed',
    statusLabel: 'OPEN SOURCE',
    category: 'systems',
    categoryLabel: 'Systems',
    accentColor: '#e2703a',
    gradient: 'linear-gradient(135deg, #f97316, #9a3412)',
    stack: ['Rust', 'HNSW vector index', 'SIMD / AVX2', 'MVCC', 'B+-tree', 'WAL', 'WebAssembly', 'Postgres wire'],
    features: [
      'Page-based storage with checksums + buffer pool',
      'B+-tree indexes with range scans',
      'Write-ahead logging with crash-injection recovery',
      'MVCC snapshot-isolation transactions',
      'System-R cost-based optimizer with EXPLAIN',
      'Hand-rolled PostgreSQL wire protocol (real psql connects)',
      'HNSW vector index with filtered k-NN + AVX2 SIMD',
    ],
    metrics: '122 tests · 9 milestones · recall@10 ~0.99 · 7–9× SIMD over scalar',
    challenge:
      "\"Can you build the machine, not just glue one together?\" Most application developers never touch the layer beneath the ORM, storage engines, query planners, concurrency control, and index structures stay black boxes. FerroDB is the answer to that question: a database with no shortcuts, where every subsystem a production DB hides is instead built from first principles, tested, and documented in the open, including the modern piece everyone now needs, a vector index.",
    approach:
      "I built FerroDB bottom-up in Rust, one subsystem at a time: a page-based pager with checksums and a buffer pool, B+-tree indexes, write-ahead logging validated against crash-injection tests, MVCC snapshot isolation, a System-R-style cost-based optimizer with EXPLAIN, and a hand-rolled slice of the PostgreSQL wire protocol that a real psql client connects to. The ninth milestone makes it AI-native: an HNSW approximate-nearest-neighbor index, the algorithm behind pgvector, Qdrant, and FAISS, implemented from the paper as a secondary index. Vector search returns row keys, the B+-tree resolves rows, and MVCC filters deleted ghosts at fetch. The hot distance loop is hand-written AVX2 with runtime CPU dispatch, and filtered vector search threads WHERE predicates into the graph traversal to dodge the post-filter recall cliff.",
    results:
      "FerroDB is public on GitHub with green CI, a 122-test suite, a published architecture book, and a live WebAssembly playground with a B+-tree visualizer. Correctness is measured, not asserted: a recall harness benchmarks the vector index against brute-force ground truth (recall@10 reaches ~0.99), and the SIMD kernels are property-tested at 7–9× over scalar, with the debugging trail documented, including the time the benchmark itself was lying. It's the portfolio piece that preempts \"did you actually build this?\" before anyone asks.",
    featureDetails: [
      {
        title: 'HNSW Vector Index',
        description:
          'An approximate-nearest-neighbor graph index implemented from the paper as a secondary index, the algorithm behind pgvector, Qdrant, and FAISS. Vector search returns row keys; the B+-tree resolves rows; MVCC filters deleted ghosts at fetch.',
      },
      {
        title: 'Filtered Vector Search',
        description:
          'WHERE + k-NN resolved in one engine. Predicates are threaded into the graph traversal instead of applied afterward, dodging the recall cliff that post-filtering causes at high selectivity.',
      },
      {
        title: 'Hand-Written AVX2 SIMD',
        description:
          'The hot distance loop (L2, dot, cosine) is hand-vectorized with runtime CPU dispatch, measured at 7–9× over scalar, property-tested to match the scalar reference exactly in ordering.',
      },
      {
        title: 'Measured Recall, Not Asserted',
        description:
          'A recall harness benchmarks the index against brute-force ground truth across ef_search settings; recall@10 reaches ~0.99. The debugging trail is documented, including the time the benchmark itself was lying.',
      },
      {
        title: 'PostgreSQL Wire Protocol',
        description:
          'A hand-rolled implementation of enough of the Postgres frontend/backend protocol that the real psql client connects, runs queries, and gets results back, no libpq shim, just the bytes on the wire.',
      },
      {
        title: 'WAL, MVCC & Crash Recovery',
        description:
          'Write-ahead logging with a recovery path proven by crash-injection tests, plus MVCC snapshot isolation so readers never block writers and every transaction sees a stable view.',
      },
    ],
    featured: false,
    order: 4,
    mockup: 'browser',
    liveUrl: null,
    links: [
      { label: 'Code', url: 'https://github.com/alemorale7777/ferrodb' },
      { label: 'Architecture book', url: 'https://alemorale7777.github.io/ferrodb/' },
      { label: 'Design Q&A', url: 'https://github.com/alemorale7777/ferrodb/blob/master/docs/interview-qa-vector-index.md' },
    ],
  },
  {
    slug: 'cannatrack',
    name: 'CannaTrack',
    initials: 'CT',
    tagline: 'Cannabis Distribution Management',
    description:
      'Enterprise-grade distribution management system with METRC/batch tracking integration for regulatory compliance. Features 280E tax compliance engine, SHA-256 authentication, and complete Clients/Distributors/Consignments workflow modules.',
    domain: null,
    status: 'deployed',
    statusLabel: 'ENTERPRISE',
    category: 'compliance',
    categoryLabel: 'Compliance',
    accentColor: 'var(--canna-green)',
    gradient: 'linear-gradient(135deg, #65a30d, #15803d)',
    stack: ['Next.js', 'React', 'Prisma', 'Neon', 'Vercel'],
    features: [
      'METRC/batch tracking integration',
      '280E tax compliance engine',
      'SHA-256 authentication system',
      'Clients/Distributors/Consignments modules',
      'Consignment tracking with employee dispatch',
      'Dark industrial branded UI',
    ],
    metrics: 'Built for regulatory compliance at scale',
    challenge:
      "Cannabis distribution operates inside one of the most regulated supply chains in the US. Every transfer must reconcile with state METRC tracking, every dollar must account for IRS Section 280E (which disallows standard business deductions for plant-touching operators), and every shipment needs a verifiable paper trail. Most operators cobble this together from spreadsheets, generic ERPs, and custom scripts, a compliance audit waiting to happen.",
    approach:
      "CannaTrack is an enterprise-grade distribution management system built around compliance as a first-class feature, not an afterthought. METRC/batch tracking is wired into the core transaction model, the 280E tax engine categorizes every expense by deductibility at the moment it's recorded, and SHA-256 authentication protects sensitive regulatory data. The UI adopts a dark industrial aesthetic that fits warehouse/office environments and stays readable under long shifts.",
    results:
      "CannaTrack is deployed as an enterprise solution with complete Clients, Distributors, and Consignments modules plus employee-dispatch consignment tracking. The system is architected to scale with multi-location operators while keeping compliance traceable end-to-end.",
    featureDetails: [
      {
        title: 'METRC / Batch Tracking',
        description:
          'Integrated with state seed-to-sale tracking requirements so every inventory movement produces a compliant, auditable record.',
      },
      {
        title: '280E Tax Compliance Engine',
        description:
          'Expenses are categorized at entry as deductible vs. non-deductible under IRS 280E, eliminating year-end reclassification chaos.',
      },
      {
        title: 'SHA-256 Authentication',
        description:
          'Hardened password hashing and session handling to protect regulated customer, distributor, and financial data.',
      },
      {
        title: 'Consignment Module',
        description:
          'Full consignment lifecycle: create, dispatch to employees, track in-transit, reconcile on return, with audit log at every step.',
      },
      {
        title: 'Clients & Distributors',
        description:
          'Separate workflows for downstream clients and upstream distributors with license verification fields and compliance status.',
      },
      {
        title: 'Industrial Dark UI',
        description:
          'Custom dark theme built for warehouse and back-office environments, high contrast, low eye strain, readable under fluorescent lighting.',
      },
    ],
    featured: false,
    order: 5,
    mockup: 'browser',
    liveUrl: null,
  },
  {
    slug: 'justice-ledger',
    name: 'Justice Ledger',
    initials: 'JL',
    tagline: 'Legal Practice Management SaaS, dual Anthropic + OpenAI legal research',
    description:
      'Practice management platform for solo attorneys and small law firms with case management, client intake, billing, and document workflows. Turborepo monorepo architecture with integrated Anthropic and OpenAI SDKs for AI-powered legal research features. Includes a comprehensive security audit identifying 32 data-touching API routes requiring auth remediation.',
    domain: null,
    status: 'deployed',
    statusLabel: 'BUILT',
    category: 'saas',
    categoryLabel: 'SaaS',
    accentColor: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    stack: [
      'Next.js 16',
      'Prisma',
      'Neon',
      'NextAuth',
      'Turborepo',
      'TypeScript',
      'Anthropic SDK',
      'OpenAI SDK',
    ],
    features: [
      'Case management with status tracking and deadlines',
      'Client intake and profile management',
      'Billing and invoice generation',
      'Document workflow management',
      'AI-powered legal research via Anthropic and OpenAI SDKs',
      'Turborepo monorepo architecture for scalability',
      'Full security audit with 6-sprint remediation roadmap',
    ],
    metrics: 'Designed for acquisition readiness (Clio, AffiniPay targets)',
    challenge:
      'Solo attorneys and small firms are underserved by legal tech. Enterprise platforms like Clio and PracticePanther are expensive and bloated. These lawyers need a modern, affordable practice management tool that handles case tracking, client intake, billing, and documents, with AI-powered research capabilities baked in, not bolted on.',
    approach:
      'I built Justice Ledger as a Turborepo monorepo with Next.js 16 App Router, giving it a scalable architecture from day one. The standout feature is dual AI SDK integration, both Anthropic and OpenAI, for legal research assistance. I also conducted a full security audit that identified 9 vulnerabilities and 10 README misrepresentations across 32 data-touching API routes, producing a complete 6-sprint remediation plan.',
    results:
      'Justice Ledger demonstrates full-stack architecture at enterprise scale with AI integration. The security audit alone showcases the depth of technical analysis I bring, identifying vulnerabilities before they become incidents. Designed with acquisition readiness in mind for legal tech acquirers.',
    featureDetails: [
      {
        title: 'AI-Powered Legal Research',
        description:
          'Dual integration with Anthropic and OpenAI SDKs for intelligent case research, document summarization, and legal question answering.',
      },
      {
        title: 'Case Management',
        description:
          'Full case lifecycle tracking with status workflows, deadline management, document attachments, and activity logging.',
      },
      {
        title: 'Client Intake System',
        description:
          'Structured client onboarding with intake forms, conflict checking, and automated profile creation.',
      },
      {
        title: 'Billing & Invoicing',
        description:
          'Time tracking, invoice generation, and payment tracking for legal billing workflows.',
      },
      {
        title: 'Security Audit Framework',
        description:
          'Identified 9 security vulnerabilities across 32 API routes with a prioritized 6-sprint remediation roadmap covering auth hardening and data protection.',
      },
      {
        title: 'Turborepo Architecture',
        description:
          'Monorepo structure with shared packages, enabling code reuse across frontend and backend while maintaining clean separation of concerns.',
      },
    ],
    featured: false,
    order: 6,
    mockup: 'browser',
    liveUrl: null,
  },
  {
    slug: 'thraxon',
    name: 'THRAXON',
    initials: 'TX',
    tagline: '2D Strategy Conquest Game',
    description:
      'Canvas-based node-conquest strategy game with 10 playable empires, 3 map sizes, CONQUEST and REGICIDE game modes. Full progression system with ranked seasons, 120-map campaign, and competitive tournament mode.',
    domain: 'thraxon.app',
    status: 'deployed',
    statusLabel: 'DEPLOYED',
    category: 'gamedev',
    categoryLabel: 'Game Dev',
    accentColor: 'var(--thraxon-red)',
    gradient: 'linear-gradient(135deg, #dc2626, #991b1b)',
    stack: ['React Native', 'Expo', 'Canvas API', 'Vercel'],
    features: [
      '10 playable empires with unique traits',
      '3 map sizes and 2 game modes',
      '120-map single player campaign',
      'Ranked seasons and tournaments',
      'Canvas-based real-time strategy rendering',
    ],
    metrics: 'Full progression + competitive multiplayer',
    challenge:
      "Mobile strategy games have regressed into energy timers, paywalls, and shallow tap-to-win loops. There's a gap in the market for a genuinely deep, canvas-based conquest game that respects the player's time, one where skill actually matters, matches have a clear start and end, and the meta-game (ranked seasons, a campaign, tournaments) rewards long-term engagement without predatory monetization.",
    approach:
      "THRAXON is a 2D node-conquest strategy game built on React Native + Expo, with a Canvas-based rendering layer for real-time strategic play. The design supports 10 playable empires with distinct traits, three map sizes, and two core modes (CONQUEST and REGICIDE). A 120-map single-player campaign teaches mechanics progressively, while ranked seasons and tournaments provide the competitive ceiling. The entire loop, tap, resolve, advance, is designed for mobile-first 5-to-15-minute sessions.",
    results:
      "THRAXON is deployed to thraxon.app with its full progression system: campaign, ranked seasons, and tournament mode. The architecture proves that genuinely strategic mobile games can run smoothly on canvas without native game engines.",
    featureDetails: [
      {
        title: '10 Playable Empires',
        description:
          'Each empire has distinct traits that shift strategic priorities, no mirror-match meta, rock-paper-scissors depth across matchups.',
      },
      {
        title: '120-Map Campaign',
        description:
          'Single-player progression that teaches mechanics through escalating puzzles and scripted encounters.',
      },
      {
        title: 'CONQUEST & REGICIDE Modes',
        description:
          'Two core win conditions: control the map (CONQUEST) or eliminate the rival leader (REGICIDE), same engine, radically different strategy.',
      },
      {
        title: 'Ranked Seasons',
        description:
          'Competitive ladder with season resets, rank decay, and tier-based matchmaking to keep the meta fresh.',
      },
      {
        title: 'Tournament Mode',
        description:
          'Structured bracket play for competitive events, bringing e-sports-style structure to mobile strategy.',
      },
      {
        title: 'Canvas Rendering',
        description:
          'Pure Canvas API renderer, no heavyweight game engine, instant launch, small bundle, 60fps on modest hardware.',
      },
    ],
    featured: false,
    order: 7,
    mockup: 'browser',
    liveUrl: 'https://thraxon.app',
  },
  {
    slug: 'soltide',
    name: 'SOLTIDE',
    initials: 'ST',
    tagline: 'F2P Mobile Farming RPG',
    description:
      'Free-to-play iOS game combining farming simulation, life sim, and action RPG mechanics in a 2.5D isometric world inspired by Hades and Cult of the Lamb. Four interconnected worlds, 25 skills with 99 mastery levels, cosmetic-only monetization.',
    domain: null,
    status: 'development',
    statusLabel: 'IN DEV',
    category: 'gamedev',
    categoryLabel: 'Game Dev',
    accentColor: 'var(--soltide-teal)',
    gradient: 'linear-gradient(135deg, #059669, #0d9488)',
    stack: ['Unity 6.3 LTS', 'C#', '2.5D Isometric'],
    features: [
      'Four interconnected worlds via Riftgate portal',
      '25 skills with 99 mastery levels',
      'RuneScape-inspired progression depth',
      'Cosmetic-only monetization model',
      'Hades/Cult of the Lamb art direction',
    ],
    metrics: 'RuneScape meets Stardew Valley meets Hades',
    challenge:
      "Free-to-play mobile RPGs are overwhelmingly pay-to-win. Monetization ate design. What's missing from the market is a genuinely deep F2P game that combines the progression depth of an MMO, the cozy loop of a farming sim, and the moment-to-moment combat satisfaction of a modern action RPG, monetized fairly through cosmetics, not power.",
    approach:
      "SOLTIDE is a 2.5D isometric farming RPG built in Unity 6.3 LTS targeting iOS first. The world is divided into four interconnected biomes connected by a central Riftgate portal. Progression runs on 25 skills each with 99 mastery levels, inspired by the RuneScape model but adapted for mobile pacing. The art direction takes cues from Hades and Cult of the Lamb, hand-painted, moody, readable at small screen sizes. Monetization is strictly cosmetic: zero power sold, ever.",
    results:
      "SOLTIDE is in active development. The combination of cozy-sim farming, RPG progression depth, and mobile-first design fills a real gap, the production pipeline is set up to iterate quickly on the four worlds and skill systems before a polished F2P launch.",
    featureDetails: [
      {
        title: 'Four Interconnected Worlds',
        description:
          'Distinct biomes unified by a central Riftgate portal, progression gates content, exploration rewards discovery.',
      },
      {
        title: '25 Skills, 99 Levels Each',
        description:
          'RuneScape-depth progression: combat, gathering, crafting, artisan, and support skills that interlock across activities.',
      },
      {
        title: '2.5D Isometric Art',
        description:
          'Hand-painted style inspired by Hades and Cult of the Lamb, rich atmosphere that reads cleanly on small mobile screens.',
      },
      {
        title: 'Cosmetic-Only Monetization',
        description:
          'No power sold, ever. Revenue comes from skins, pets, and cosmetic housing items, respects player investment.',
      },
      {
        title: 'Farming + Action RPG Loop',
        description:
          'Cozy farming and life-sim mechanics sit alongside satisfying real-time combat, two loops reinforcing the same progression.',
      },
      {
        title: 'Unity 6.3 LTS Foundation',
        description:
          'Built on the latest LTS for long-term stability, with an iOS-first pipeline targeting App Store release.',
      },
    ],
    featured: false,
    order: 8,
    mockup: 'phone',
    liveUrl: null,
  },
  {
    slug: 'wedding-site',
    name: 'Client Wedding Website',
    initials: 'CW',
    tagline: 'Production Freelance Project',
    description:
      'Complete production-deployed wedding website for a paying client. Mountain/dark-mode aesthetic with custom blizzard particle effects, SVG mountain range, countdown timer. Full RSVP system, livestream integration, guestbook, interactive quiz, and photo booth.',
    domain: null,
    status: 'live',
    statusLabel: 'DELIVERED',
    category: 'freelance',
    categoryLabel: 'Freelance',
    accentColor: '#94a3b8',
    gradient: 'linear-gradient(135deg, #475569, #1e293b)',
    stack: ['Next.js', 'Prisma', 'Neon', 'Vercel', 'Tailwind'],
    features: [
      'Custom blizzard particle system',
      'SVG mountain range with parallax',
      'RSVP system with meal selection',
      'Livestream integration for remote guests',
      'Interactive quiz and photo booth',
      'Post-wedding conditional features',
    ],
    metrics: 'Delivered on deadline for April 2026 event',
    challenge:
      "Wedding websites from off-the-shelf templates all look the same: pastel flowers, generic layouts, no personality. This client wanted a custom site that actually reflected the feel of their mountain wedding, and that could serve as the hub for everything guests needed before, during, and after the day: RSVPs, livestream, schedule, photos. One platform, not five.",
    approach:
      "I built a bespoke Next.js site with a dark mountain-mode aesthetic: SVG mountain range with parallax, custom-written blizzard particle system, and a live countdown timer that re-theme itself post-wedding. On top of the design sits a real application, RSVP system with meal selection (backed by Prisma + Neon), livestream embed for remote guests, guestbook, an interactive quiz, and a photo booth feature. The entire site shipped on Vercel on a hard calendar deadline.",
    results:
      "Delivered on-time and on-budget for the April 2026 event. The site replaced what would normally be 3-4 separate tools (RSVP service, livestream platform, guestbook, photo gallery) with one branded experience, and proved the freelance delivery model end-to-end.",
    featureDetails: [
      {
        title: 'Blizzard Particle System',
        description:
          'Custom-written canvas snow/wind system that sets the mountain mood without overwhelming the content.',
      },
      {
        title: 'SVG Mountain Parallax',
        description:
          'Hand-crafted SVG mountain range with depth-staged parallax, subtle, performant, unmistakably on-theme.',
      },
      {
        title: 'RSVP with Meal Selection',
        description:
          'Database-backed RSVP flow (Prisma + Neon) capturing guest count, dietary restrictions, and meal preferences.',
      },
      {
        title: 'Livestream Integration',
        description:
          'Embedded livestream page for remote guests with chat and countdown, no separate platform logins.',
      },
      {
        title: 'Interactive Quiz + Photo Booth',
        description:
          'Two engagement features that turn the site into an event in its own right, not just an info page.',
      },
      {
        title: 'Post-Wedding Conditional Mode',
        description:
          'Content, hero, and CTAs swap automatically after the event date, the site gracefully transitions from pre- to post-wedding.',
      },
    ],
    featured: false,
    order: 9,
    mockup: 'browser',
    liveUrl: null,
  },
];

export type FilterCategory = ProjectCategory | 'all';

export const FILTER_TABS: { value: FilterCategory; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'saas', label: 'SaaS' },
  { value: 'fintech', label: 'FinTech' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'gamedev', label: 'Game Dev' },
  { value: 'systems', label: 'Systems' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'freelance', label: 'Freelance' },
];

export function filterProjects(projects: Project[], filter: FilterCategory): Project[] {
  if (filter === 'all') return projects;
  return projects.filter((p) => p.category === filter);
}

export function getProjectBySlug(slug: string): Project | undefined {
  return PROJECTS.find((p) => p.slug === slug);
}

export function getAdjacentProjects(slug: string): { prev: Project; next: Project } | null {
  const ordered = [...PROJECTS].sort((a, b) => a.order - b.order);
  const idx = ordered.findIndex((p) => p.slug === slug);
  if (idx === -1) return null;
  const prev = ordered[(idx - 1 + ordered.length) % ordered.length];
  const next = ordered[(idx + 1) % ordered.length];
  return { prev, next };
}
